'use client'

import * as React from 'react'
import { ArrowLeftIcon } from 'lucide-react'

import { cn } from '../lib/utils'
import { Button } from './button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './dialog'

/**
 * FlowDialog — a Dialog for flows that render MORE THAN ONE thing for a single
 * context: a choose → configure step, a wizard, an inspector with sub-views.
 * It owns the two things every such flow needs and every hand-rolled version
 * gets subtly wrong:
 *
 *  1. **A back affordance** — pass `onBack` and a back button appears in the
 *     header, so sub-views return to the root without each re-inventing it.
 *  2. **A soft height shift** — as the body content grows/shrinks (a taller
 *     view, a form revealing fields), the dialog animates instead of snapping.
 *
 * How the animation works (important): we do NOT set the dialog's own height.
 * An **inner container** animates its height to match its content (measured via
 * a ResizeObserver attached through a callback ref, so it's robust to the
 * portal mount timing), capped at a fraction of the viewport and scrolling past
 * that (with a CSS `max-height` safety so the dialog can never exceed the
 * screen). Because the height is always derived from the live content, the
 * dialog can't get stuck at a stale size when a view swaps or it's reopened.
 * Honours `prefers-reduced-motion` and never animates on first open. Body
 * content should carry its own padding (`px-4 py-4`).
 */
export interface FlowDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  /** When provided, a back button is shown at the start of the header. */
  onBack?: () => void
  /** Body height cap as a fraction of the viewport height. Default `0.7`. */
  maxBodyHeightRatio?: number
  /** Extra classes for the DialogContent surface. */
  className?: string
  children: React.ReactNode
}

function getCapPx(ratio: number): number {
  if (typeof window === 'undefined') {
    return 600
  }
  return Math.round(window.innerHeight * ratio)
}

export function FlowDialog({
  open,
  onOpenChange,
  title,
  description,
  onBack,
  maxBodyHeightRatio = 0.7,
  className,
  children,
}: FlowDialogProps): React.JSX.Element {
  const capRef = React.useRef(getCapPx(maxBodyHeightRatio))
  const observerRef = React.useRef<ResizeObserver | null>(null)
  const [height, setHeight] = React.useState<number | undefined>(undefined)
  const [animate, setAnimate] = React.useState(false)

  // Keep the viewport cap current.
  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const update = () => {
      capRef.current = getCapPx(maxBodyHeightRatio)
    }
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('resize', update)
    }
  }, [maxBodyHeightRatio])

  // Callback ref: attach a ResizeObserver whenever the content element mounts
  // (i.e. when the portal opens), so we don't miss it due to effect/portal
  // timing. The observer keeps the inner height in sync with the live content.
  const setContentRef = React.useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    if (!el) {
      return
    }
    const measure = () => {
      setHeight(Math.min(el.scrollHeight, capRef.current))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    observerRef.current = observer
  }, [])

  // Reset when closed so a reopen re-measures fresh (and never animates open).
  React.useEffect(() => {
    if (!open) {
      setAnimate(false)
      setHeight(undefined)
    }
  }, [open])

  // Enable the transition only after the first height lands.
  React.useEffect(() => {
    if (height === undefined) {
      return
    }
    const id = requestAnimationFrame(() => {
      setAnimate(true)
    })
    return () => {
      cancelAnimationFrame(id)
    }
  }, [height])

  React.useEffect(() => {
    return () => {
      observerRef.current?.disconnect()
    }
  }, [])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg',
          className
        )}
      >
        <DialogHeader className="flex-shrink-0 border-b border-border p-4 pr-10">
          <div className="flex items-center gap-2">
            {onBack ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Back"
                className="shrink-0"
                onClick={onBack}
              >
                <ArrowLeftIcon />
              </Button>
            ) : null}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <DialogTitle className="truncate">{title}</DialogTitle>
              {description ? (
                <DialogDescription>{description}</DialogDescription>
              ) : null}
            </div>
          </div>
        </DialogHeader>

        <div
          className={cn(
            // `min-h-0` lets the flex child shrink; `max-h` is the hard safety
            // so the body can never push the dialog past the screen even if the
            // measured height hasn't landed yet.
            'min-h-0 max-h-[75vh] overflow-y-auto scroll-py-4',
            animate &&
              'transition-[height] duration-200 ease-out motion-reduce:transition-none'
          )}
          style={{ height }}
        >
          <div ref={setContentRef}>{children}</div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
